// GENERATED — DO NOT HAND-EDIT.
//
// Source of truth: specs/tier-05-experience/template.entity-hint.yaml
// Generator: tools/v2-gen/v2gen.py
// Regenerate via: make v2-generate-screens
//
// Hand-edits to non-override-block content WILL be lost on next
// regeneration. See specs/_product/dashboard-v2-page-generator.md §5
// for override blocks (imports / helpers / actions / custom-tabs).

import type { DetailPageConfig } from "../../components/composition/DetailPage";
import { templatesApi } from "../../api/templates";
import type { Template } from "../../api/types";
// override-block:imports BEGIN
import { createElement, useEffect, useState } from "react";
// CHG-1285 — closes CHG-1261 Editor-tab view-only forward-promise.
import { TemplateEditor } from "../../components/template-editor/TemplateEditor";
import type {
  TemplateStatus,
  TemplateTone,
  TemplateMessageBinding,
} from "../../api/types";
// CHG-1294 — per-page RBAC gate wiring (consumes GATE_CATALOG).
import { GATE_CATALOG } from "../../auth/gateCatalog";
import { toGate } from "../../auth/toGateProp";
// override-block:imports END

// override-block:helpers BEGIN
const TONE_LABEL: Record<TemplateTone, string> = {
  warm: "Warm",
  urgent: "Urgent",
  celebratory: "Celebratory",
  concerned: "Concerned",
  grateful: "Grateful",
};
// Status → stat card tone. Published is celebratory (in-use), draft is
// neutral (in-progress), archived is muted (out-of-loop). Same per-row
// tone override pattern as CHG-1243 donation status card.
const STATUS_TONE: Record<TemplateStatus, "g-giving" | "g-home" | "g-settings"> = {
  published: "g-giving",
  draft: "g-home",
  archived: "g-settings",
};

function dashSpan(): ReturnType<typeof createElement> {
  return createElement("span", { style: { color: "var(--color-text-muted)" } }, "—");
}

// BindingsList — back-reference index grouped by message_kind. Source:
// /templates/{id}/messages.
function BindingsList({ templateId }: { templateId: string }) {
  const [state, setState] = useState<{
    loading: boolean;
    error: Error | null;
    bindings: TemplateMessageBinding[];
  }>({ loading: true, error: null, bindings: [] });

  useEffect(() => {
    let cancelled = false;
    templatesApi.listTemplateBindings(templateId).then(
      (r) => {
        if (cancelled) return;
        setState({ loading: false, error: null, bindings: r.items });
      },
      (err) => {
        if (cancelled) return;
        setState({ loading: false, error: err, bindings: [] });
      },
    );
    return () => { cancelled = true; };
  }, [templateId]);

  if (state.loading) {
    return createElement(
      "div",
      { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
      "Loading bindings…",
    );
  }
  if (state.error) {
    return createElement(
      "div",
      { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
      "Failed to load bindings.",
    );
  }
  if (state.bindings.length === 0) {
    return createElement(
      "div",
      { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
      "Not bound to any messages yet.",
    );
  }
  const byKind = new Map<string, TemplateMessageBinding[]>();
  for (const b of state.bindings) {
    const list = byKind.get(b.message_kind) ?? [];
    list.push(b);
    byKind.set(b.message_kind, list);
  }
  return createElement(
    "div",
    { style: { display: "flex", flexDirection: "column", gap: 8 } },
    Array.from(byKind.entries()).map(([kind, bindings]) =>
      createElement(
        "div",
        { key: kind, className: "card", style: { padding: 16 } },
        createElement("div", { style: { fontWeight: 600, marginBottom: 4 } }, kind),
        createElement(
          "div",
          { style: { fontSize: 13, color: "var(--color-text-muted)" } },
          `${bindings.length} message${bindings.length === 1 ? "" : "s"} use${bindings.length === 1 ? "s" : ""} this template`,
        ),
      ),
    ),
  );
}
// override-block:helpers END

export const templateDetailConfig: DetailPageConfig<Template> = {
  idParam: "templateId",
  queryKey: (id: string) => ["templates", "detail", id] as const,
  queryFn: (id: string) => templatesApi.getTemplate(id),
  backTo: "/templates",
  // override-block:title BEGIN
  title: (r) => r.name,
  // override-block:title END
  // override-block:subtitle BEGIN
  subtitle: (r) => r.slug ?? null,
  // override-block:subtitle END
  statusCards: (r, _fmt) => [
    // override-block:statcard-uses BEGIN
    {
      id: "uses",
      label: "Uses",
      value: r.uses_count ?? null,
      format: r.uses_count != null ? "number" : "text",
      tone: "g-giving",
      render: r.uses_count == null ? dashSpan() : undefined,
    },
    // override-block:statcard-uses END
    // override-block:statcard-last-modified BEGIN
    {
      id: "last_modified",
      label: "Last modified",
      value: r.updated_at,
      format: "text",
      tone: "g-home",
      render: createElement(
        "span",
        { className: "tabular" },
        _fmt.formatDate(r.updated_at, "relative"),
      ),
    },
    // override-block:statcard-last-modified END
    // override-block:statcard-tone BEGIN
    {
      id: "tone",
      label: "Tone",
      value: TONE_LABEL[r.tone] ?? r.tone,
      format: "text",
      tone: "g-mygiving",
    },
    // override-block:statcard-tone END
    // override-block:statcard-version BEGIN
    {
      id: "version",
      label: "Version",
      value: r.current_version,
      format: "text",
      tone: STATUS_TONE[r.status] ?? "g-events",
    },
    // override-block:statcard-version END
  ],
  actions: (_r) => [
    // override-block:actions BEGIN
    { id: "edit", label: "Edit", variant: "secondary", permissionGate: toGate(GATE_CATALOG.template_detail["edit"], "disable") },
    { id: "publish", label: "Publish", variant: "primary", permissionGate: toGate(GATE_CATALOG.template_detail["publish"], "hide") },
    { id: "test-send", label: "Test send", variant: "ghost", permissionGate: toGate(GATE_CATALOG.template_detail["test-send"], "hide") },
    // override-block:actions END
  ],
  tabs: [
    // override-block:custom-tabs BEGIN
    // 4 tabs per template-detail-v2.cross-screen.yaml. Editor uses
    // TemplateEditor (CHG-1285 view+edit modes). History + Test are
    // placeholder bodies. Bindings uses BindingsList helper above.
    {
      id: "editor",
      label: "Editor",
      render: (t) => createElement(TemplateEditor, { template: t }),
    },
    {
      id: "history",
      label: "History",
      render: (t) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Version history — fetched via GET /templates/${t.id}/versions. ` +
            "Most-recent-first; each row shows version + subject + created_at + " +
            "created_by. Full UI in follow-up ε CHG.",
        ),
    },
    {
      id: "test",
      label: "Test",
      render: (t) =>
        createElement(
          "div",
          { className: "card", style: { padding: 16, color: "var(--color-text-muted)" } },
          `Send a test render of this template to a chosen recipient via ` +
            `POST /templates/${t.id}/test-send. Variable substitution form + ` +
            "channel picker. Full UI in follow-up ε CHG.",
        ),
    },
    {
      id: "bindings",
      label: "Bindings",
      render: (t) => createElement(BindingsList, { templateId: t.id }),
    },
    // override-block:custom-tabs END
  ],
};
